import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Container,
  IconButton,
  useDisclosure,
  HStack,
  VStack,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useBreakpointValue,
  Tooltip,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { FaGithub } from 'react-icons/fa';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const NavLink = ({ children, to, onClick, isExternal = false }) => {
  const linkColor = 'gray.300';
  const linkHoverColor = 'white';
  const location = useLocation();

  const LinkComponent = isExternal ? RouterLink : (location.pathname === '/' ? ScrollLink : RouterLink);
  const linkProps = isExternal ? { to } : (location.pathname === '/' ? { to, smooth: true, duration: 600, offset: -80 } : { to: `/#${to}` });

  return (
    <LinkComponent {...linkProps} onClick={onClick}>
      <Box
        as="span"
        px={3}
        py={2}
        position="relative"
        fontWeight="medium"
        color={linkColor}
        _hover={{
          color: linkHoverColor,
          _after: {
            width: '100%',
          }
        }}
        _after={{
          content: '""',
          position: 'absolute',
          width: '0%',
          height: '2px',
          bottom: '0',
          left: '50%',
          background: 'white',
          transition: 'all 0.3s ease-in-out',
          transform: 'translateX(-50%)',
        }}
        transition="all 0.3s ease-in-out"
      >
        {children}
      </Box>
    </LinkComponent>
  );
};

export default function Header() {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [scrolled, setScrolled] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Features', to: 'features' },
    { name: 'Statistiken', to: 'statistics' },
    { name: 'Preise', to: 'pricing' },
  ];

  const bgColor = 'rgba(18, 18, 18, 0.8)';
  const borderColor = 'gray.800';

  return (
    <MotionBox
      as="header"
      position="fixed"
      w="100%"
      bg={bgColor}
      backdropFilter="blur(10px)"
      boxShadow={scrolled ? "md" : "none"}
      zIndex="sticky"
      borderBottom="1px"
      borderColor={borderColor}
      transition="all 0.3s"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
    >
      <Container maxW="container.xl">
        <Flex
          minH={'80px'}
          py={{ base: 2 }}
          px={{ base: 4 }}
          align={'center'}
          justify={'space-between'}
        >
          <HStack spacing={6} flex={1} justify="flex-start">
            <AnimatePresence>
              {navItems.map((item, index) => (
                <MotionBox
                  key={item.to}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <NavLink to={item.to} isExternal={item.isExternal}>
                    {item.name}
                  </NavLink>
                </MotionBox>
              ))}
            </AnimatePresence>
          </HStack>

          <HStack spacing={2}>
            <Tooltip label="GitHub Repository" aria-label="GitHub Repository">
              <IconButton
                as="a"
                href="https://github.com/storminator89/Zeiterfassung"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub Repository"
                icon={<FaGithub />}
                variant="ghost"
                color="gray.300"
                _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
                transition="all 0.3s ease-in-out"
              />
            </Tooltip>
            {isMobile && (
              <IconButton
                display={{ base: 'flex', md: 'none' }}
                onClick={onToggle}
                icon={
                  isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
                }
                variant={'ghost'}
                aria-label={'Toggle Navigation'}
                color="gray.300"
                _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
                transition="all 0.3s ease-in-out"
              />
            )}
          </HStack>
        </Flex>

        <Drawer placement="right" onClose={onClose} isOpen={isOpen}>
          <DrawerOverlay />
          <DrawerContent bg={bgColor} color="gray.300">
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">Navigation</DrawerHeader>
            <DrawerBody>
              <VStack spacing={4} align="stretch">
                {navItems.map((item) => (
                  <Flex
                    key={item.to}
                    align="center"
                    minH="40px" // Ensure consistent height
                  >
                    <NavLink to={item.to} onClick={onClose} isExternal={item.isExternal}>
                      {item.name}
                    </NavLink>
                  </Flex>
                ))}
                <Flex align="center" minH="40px">
                  <NavLink
                    to="https://github.com/storminator89/Zeiterfassung"
                    onClick={onClose}
                    isExternal={true}
                  >
                    GitHub
                  </NavLink>
                </Flex>
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Container>
    </MotionBox>
  );
}
